import { Loader2, RefreshCw, BarChart3, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const UsageMonitor = ({ usageStats, loadingUsage, onRefresh, wallet }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="risk-usage-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
          <BarChart3 className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">TelcaVoIP API Usage Monitor</h2>
          <p className="text-xs text-slate-500">Monitoraggio utilizzo e costi</p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={onRefresh} disabled={loadingUsage}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loadingUsage ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {loadingUsage && !usageStats ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : usageStats ? (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <p className="text-xs font-medium text-blue-600 uppercase">Questo Mese</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{usageStats.month.total_calls}</p>
              <p className="text-xs text-blue-500 mt-1">chiamate API</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
              <p className="text-xs font-medium text-emerald-600 uppercase">Spesa Mese</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1">{usageStats.month.total_cost.toFixed(2)}€</p>
              <p className="text-xs text-emerald-500 mt-1">solo riuscite</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
              <p className="text-xs font-medium text-amber-600 uppercase">Oggi</p>
              <p className="text-2xl font-bold text-amber-900 mt-1">{usageStats.today.total_calls}</p>
              <p className="text-xs text-amber-500 mt-1">{usageStats.today.total_cost.toFixed(2)}€</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-100">
              <p className="text-xs font-medium text-purple-600 uppercase">Totale Storico</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">{usageStats.all_time.total_cost.toFixed(2)}€</p>
              <p className="text-xs text-purple-500 mt-1">{usageStats.all_time.total_calls} chiamate</p>
            </div>
          </div>

          {/* Success Rate Bar */}
          {usageStats.month.total_calls > 0 && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-600 uppercase">Tasso di Successo (Mese)</span>
                <span className="text-sm font-bold text-slate-800">
                  {Math.round((usageStats.month.successful / usageStats.month.total_calls) * 100)}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div className="bg-emerald-500 h-2.5 rounded-full transition-all" style={{ width: `${(usageStats.month.successful / usageStats.month.total_calls) * 100}%` }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-emerald-600">{usageStats.month.successful} riuscite</span>
                <span className="text-xs text-red-500">{usageStats.month.failed} fallite</span>
              </div>
            </div>
          )}

          {/* Daily Chart */}
          {usageStats.daily_chart.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Andamento Ultimi 30 Giorni</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usageStats.daily_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      formatter={(val, name) => [name === 'cost' ? `${val}€` : val, name === 'cost' ? 'Costo' : name === 'success' ? 'Riuscite' : 'Fallite']}
                      labelFormatter={l => new Date(l).toLocaleDateString('it-IT')}
                    />
                    <Bar dataKey="success" fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} name="Riuscite" />
                    <Bar dataKey="failed" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} name="Fallite" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Cost Breakdown Table */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Dettaglio Costi per Tipo di Report (Mese)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Report</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Costo Unit.</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Chiamate</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-emerald-500 uppercase">Riuscite</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-red-500 uppercase">Fallite</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usageStats.month.by_type).length > 0 ? (
                    Object.entries(usageStats.month.by_type).sort((a, b) => b[1].cost - a[1].cost).map(([key, val]) => (
                      <tr key={key} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800">{val.label}</td>
                        <td className="py-2 px-3 text-center text-slate-600">{val.unit_cost.toFixed(2)}€</td>
                        <td className="py-2 px-3 text-center font-semibold">{val.calls}</td>
                        <td className="py-2 px-3 text-center text-emerald-600">{val.success}</td>
                        <td className="py-2 px-3 text-center text-red-500">{val.failed}</td>
                        <td className="py-2 px-3 text-right font-bold text-slate-800">{val.cost.toFixed(2)}€</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={6} className="py-4 text-center text-slate-400 text-xs">Nessun dato questo mese</td></tr>
                  )}
                </tbody>
                {Object.entries(usageStats.month.by_type).length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td colSpan={2} className="py-2 px-3 font-bold text-slate-700">TOTALE</td>
                      <td className="py-2 px-3 text-center font-bold">{usageStats.month.total_calls}</td>
                      <td className="py-2 px-3 text-center font-bold text-emerald-600">{usageStats.month.successful}</td>
                      <td className="py-2 px-3 text-center font-bold text-red-500">{usageStats.month.failed}</td>
                      <td className="py-2 px-3 text-right font-bold text-lg text-slate-900">{usageStats.month.total_cost.toFixed(2)}€</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* TelcaVoIP Price List */}
          <div id="risk-pricing-section" className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-md flex items-center justify-center">
                <CreditCard className="w-3 h-3 text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-800">Listino Servizi TelcaVoIP Risk Intelligence</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Servizio</th>
                    {wallet?.is_super_admin && <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">Costo Base</th>}
                    {wallet?.is_super_admin && <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">Markup</th>}
                    <th className="text-right py-2 px-3 text-xs font-semibold text-teal-600">Prezzo TelcaVoIP</th>
                  </tr>
                </thead>
                <tbody>
                  {usageStats.cost_table && Object.entries(usageStats.cost_table).map(([key, val]) => {
                    const markedUp = (val.cost_eur * (1 + (usageStats.wallet?.markup_pct || 25) / 100)).toFixed(2);
                    return (
                      <tr key={key} className="border-b border-slate-100 hover:bg-white transition-colors">
                        <td className="py-2.5 px-3 font-medium text-slate-700">{val.label}</td>
                        {wallet?.is_super_admin && <td className="py-2.5 px-3 text-center text-slate-400">{val.cost_eur.toFixed(2)}€</td>}
                        {wallet?.is_super_admin && <td className="py-2.5 px-3 text-center text-violet-500 text-xs">+{usageStats.wallet?.markup_pct || 25}%</td>}
                        <td className="py-2.5 px-3 text-right font-bold text-teal-700">{markedUp}€</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!wallet?.is_super_admin && (
              <p className="text-xs text-slate-400 mt-3 text-center">I prezzi sono IVA esclusa. Servizio erogato da TelcaVoIP.</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-8">Nessun dato disponibile</p>
      )}
    </div>
  );
};
